/**
 * Booking Helper Utilities
 * Phase 2: Frontend Service Layer
 */

import { CURRENCY_SYMBOL } from './currency';
import {
  BookingStatus,
  PaymentStatus,
  BlockReason,
  OneTimeBooking,
  RecurringBooking,
  BlockedSlot,
  ScheduleItemType,
} from '../types/bookings';

// ============================================================================
// Time Formatting
// ============================================================================

/**
 * Format time slot as readable string
 * @example formatTimeSlot('10:00:00', '11:00:00') => '10:00 AM - 11:00 AM'
 */
export function formatTimeSlot(startTime: string, endTime: string): string {
  if (!startTime || !endTime) return 'N/A';

  const formatTime = (time: string) => {
    if (!time) return 'N/A';
    const parts = time.split(':');
    if (parts.length < 2) return time;

    const [hours, minutes] = parts;
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  return `${formatTime(startTime)} - ${formatTime(endTime)}`;
}

/**
 * Format date as readable string
 * @example formatDate('2025-10-12') => 'Oct 12, 2025'
 */
export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return 'N/A';

  // Parse date string as local date to avoid timezone conversion issues
  // When backend sends "2025-10-14", we want to display Oct 14, not Oct 13
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    const year = parseInt(parts[0]);
    const month = parseInt(parts[1]) - 1; // Month is 0-indexed
    const day = parseInt(parts[2]);
    const date = new Date(year, month, day);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  // Fallback for other formats
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format single time value
 * @example formatTime('14:30:00') => '2:30 PM'
 */
export function formatTime(time: string | null | undefined): string {
  if (!time) return 'N/A';
  const parts = time.split(':');
  if (parts.length < 2) return time;

  const [hours, minutes] = parts;
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
}

/**
 * Format date and time together
 * @example formatDateTime('2025-10-12', '10:00:00', '11:00:00') => 'Oct 12, 2025 · 10:00 AM - 11:00 AM'
 */
export function formatDateTime(date: string, startTime: string, endTime: string): string {
  return `${formatDate(date)} · ${formatTimeSlot(startTime, endTime)}`;
}

/**
 * Format ISO datetime string
 * @example formatDateTimeString('2025-10-12T14:30:00.000Z') => 'Oct 12, 2025 at 2:30 PM'
 */
export function formatDateTimeString(isoString: string | null | undefined): string {
  if (!isoString) return 'N/A';

  try {
    const date = new Date(isoString);
    const dateStr = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return `${dateStr} at ${timeStr}`;
  } catch (e) {
    return 'Invalid date';
  }
}

/**
 * Get relative date string (Today, Tomorrow, Yesterday, or date)
 */
export function getRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);

  const diffDays = Math.floor((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';

  return formatDate(dateStr);
}

// ============================================================================
// Status Styling
// ============================================================================

/**
 * Get color class for booking status
 */
export function getStatusColor(status: BookingStatus | 'active' | 'checked_in' | 'released'): string {
  switch (status) {
    case 'confirmed':
    case 'paid':
      return 'bg-green-100 text-green-800';
    case 'scheduled':
      return 'bg-blue-100 text-blue-800'; // Scheduled instances shown in blue
    case 'active':
      return 'bg-green-100 text-green-800';
    case 'pending_payment':
      return 'bg-yellow-100 text-yellow-800';
    case 'completed':
      return 'bg-gray-100 text-gray-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    case 'no_show':
      return 'bg-red-100 text-red-800';
    case 'draft':
      return 'bg-gray-100 text-gray-600';
    case 'checked_in':
      return 'bg-blue-100 text-blue-800';
    case 'released':
      return 'bg-gray-100 text-gray-600';
    case 'blocked':
      return 'bg-orange-100 text-orange-800';
    case 'external':
      return 'bg-teal-100 text-teal-800'; // Third-party/partner reservations - teal for distinctive look
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

/**
 * Get icon for booking status
 */
export function getStatusIcon(status: BookingStatus | 'active' | 'checked_in' | 'released'): string {
  switch (status) {
    case 'confirmed':
    case 'paid':
      return '✓';
    case 'active':
      return '▶';
    case 'pending_payment':
      return '⏳';
    case 'completed':
      return '✓';
    case 'cancelled':
      return '✕';
    case 'no_show':
      return '⚠';
    case 'draft':
      return '○';
    case 'checked_in':
      return '✓';
    case 'released':
      return '○';
    case 'blocked':
      return '🔒';
    case 'external':
      return '🔗'; // Third-party/partner reservations
    default:
      return '•';
  }
}

/**
 * Get payment status color
 */
export function getPaymentStatusColor(status: PaymentStatus): string {
  switch (status) {
    case 'successful':
      return 'bg-green-100 text-green-800';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    case 'refunded':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

/**
 * Get color for refund status (Paystack statuses)
 */
export function getRefundStatusColor(status: string | undefined): string {
  if (!status) return 'bg-gray-100 text-gray-800';

  switch (status) {
    case 'processed':
      return 'bg-green-100 text-green-800';
    case 'processing':
      return 'bg-blue-100 text-blue-800';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    case 'not_applicable':
      return 'bg-gray-100 text-gray-600';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

/**
 * Get display label for refund status
 */
export function getRefundStatusLabel(status: string | undefined): string {
  if (!status) return 'N/A';

  switch (status) {
    case 'processed':
      return 'Refunded';
    case 'processing':
      return 'Processing';
    case 'pending':
      return 'Pending';
    case 'failed':
      return 'Failed';
    case 'not_applicable':
      return 'N/A';
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
}

/**
 * Get color for booking type
 */
export function getTypeColor(type: ScheduleItemType): string {
  switch (type) {
    case 'one_time_booking':
      return 'bg-green-500';
    case 'recurring_instance':
      return 'bg-blue-500';
    case 'blocked_slot':
      return 'bg-orange-500';
    default:
      return 'bg-gray-500';
  }
}

/**
 * Get type badge emoji
 */
export function getTypeBadge(type: ScheduleItemType): string {
  switch (type) {
    case 'one_time_booking':
      return '🟢';
    case 'recurring_instance':
      return '🔵';
    case 'blocked_slot':
      return '🟠';
    default:
      return '⚪';
  }
}

// ============================================================================
// Block Reason Formatting
// ============================================================================

/**
 * Get human-readable block reason
 */
export function getBlockReasonDisplay(reason: BlockReason, partnerName?: string | null): string {
  switch (reason) {
    case 'walk_in':
      return 'Walk-in Booking';
    case 'phone_booking':
      return 'Phone Booking';
    case 'maintenance':
      return 'Maintenance';
    case 'private_event':
      return 'Private Event';
    case 'third_party':
      return partnerName ? `Third-Party: ${partnerName}` : 'Third-Party Sync';
    case 'other':
      return 'Other';
    default:
      return reason;
  }
}

/**
 * Get Lucide icon component name for block reason
 * Returns icon name to be used with lucide-react
 */
export function getBlockReasonIcon(reason: BlockReason): string {
  switch (reason) {
    case 'walk_in':
      return 'UserCheck';
    case 'phone_booking':
      return 'Phone';
    case 'maintenance':
      return 'Wrench';
    case 'private_event':
      return 'PartyPopper';
    case 'third_party':
      return 'Globe';
    case 'other':
      return 'FileText';
    default:
      return 'Circle';
  }
}

// ============================================================================
// Recurring Booking Helpers
// ============================================================================

/**
 * Calculate progress percentage for recurring booking
 */
export function calculateProgress(completed: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((completed / total) * 100);
}

/**
 * Get next upcoming session from instances
 */
export function getNextSession(instances: any[]): any | null {
  const today = new Date().toISOString().split('T')[0];
  const upcoming = instances
    .filter((instance: any) => instance.scheduled_date >= today && instance.status !== 'cancelled')
    .sort((a: any, b: any) => a.scheduled_date.localeCompare(b.scheduled_date));

  return upcoming.length > 0 ? upcoming[0] : null;
}

/**
 * Format recurring frequency as readable string
 */
export function formatFrequency(frequency: string, daysOfWeek?: string[]): string {
  if (frequency === 'weekly' && daysOfWeek && daysOfWeek.length > 0) {
    const days = daysOfWeek
      .filter(day => day && typeof day === 'string')
      .map(day => String(day).substring(0, 3))
      .join(', ');
    if (days) {
      return `Weekly (${days})`;
    }
  }

  switch (frequency) {
    case 'weekly':
      return 'Weekly';
    case 'bi_weekly':
      return 'Bi-weekly';
    case 'monthly':
      return 'Monthly';
    default:
      return frequency;
  }
}

// ============================================================================
// Validation Helpers
// ============================================================================

/**
 * Check if booking can be checked in
 */
export function canCheckIn(booking: OneTimeBooking | any): boolean {
  if (booking.checked_in_at) return false;
  if (!['confirmed', 'paid'].includes(booking.status)) return false;

  // Manual bookings cannot be checked in
  if (booking.source === 'manual') return false;

  // Check if booking date is today or in the past
  const bookingDate = new Date(booking.booking_date || booking.scheduled_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  bookingDate.setHours(0, 0, 0, 0);

  return bookingDate <= today;
}

/**
 * Check if booking can be rescheduled
 */
export function canReschedule(booking: OneTimeBooking | RecurringBooking): boolean {
  if (!['confirmed', 'paid', 'pending_payment'].includes(booking.status)) return false;

  // Check if booking start time is in the future (allow rescheduling up to booking start time)
  const dateStr = 'booking_date' in booking ? booking.booking_date : booking.start_date;
  const timeStr = 'start_time' in booking ? booking.start_time : '00:00:00';

  // Combine date and time to get full datetime
  const bookingDateTime = new Date(`${dateStr}T${timeStr}`);
  const now = new Date();

  // Allow rescheduling if booking start time hasn't passed yet
  return bookingDateTime > now;
}

/**
 * Check if booking can be cancelled
 * Only allows cancellation if the booking's start time hasn't passed yet
 */
export function canCancel(booking: OneTimeBooking | RecurringBooking): boolean {
  if (['cancelled', 'completed', 'no_show'].includes(booking.status)) return false;

  // Check if booking start time is in the future
  const dateStr = 'booking_date' in booking ? booking.booking_date : booking.start_date;
  const timeStr = 'start_time' in booking ? booking.start_time : '00:00:00';

  // Combine date and time to get full datetime
  const bookingDateTime = new Date(`${dateStr}T${timeStr}`);
  const now = new Date();

  // Only allow cancel if booking start time hasn't passed yet
  return bookingDateTime > now;
}

/**
 * Check if blocked slot can be unblocked
 */
export function canUnblock(block: BlockedSlot): boolean {
  return block.status === 'active';
}

// ============================================================================
// Currency Formatting
// ============================================================================

/**
 * Format amount as Naira currency
 */
export function formatCurrency(amount: number | string | undefined | null): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (num === undefined || num === null || isNaN(num)) {
    return `${CURRENCY_SYMBOL}0`;
  }
  return `${CURRENCY_SYMBOL}${num.toLocaleString('en-NG', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

/**
 * Format currency with vendor share indicator
 */
export function formatVendorShare(venueShare: number, platformCommission: number): string {
  const total = venueShare + platformCommission;
  const percentage = Math.round((venueShare / total) * 100);
  return `${formatCurrency(venueShare)} (${percentage}% yours)`;
}

// ============================================================================
// Grouping and Sorting
// ============================================================================

/**
 * Group bookings by date
 */
export function groupByDate<T extends { booking_date?: string; scheduled_date?: string; date?: string }>(
  items: T[]
): Record<string, T[]> {
  return items.reduce((groups: Record<string, T[]>, item) => {
    const date = item.booking_date || item.scheduled_date || item.date || '';
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(item);
    return groups;
  }, {});
}

/**
 * Sort bookings by date and time
 */
export function sortByDateTime<T extends {
  booking_date?: string;
  scheduled_date?: string;
  date?: string;
  start_time: string;
}>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const dateA = a.booking_date || a.scheduled_date || a.date || '';
    const dateB = b.booking_date || b.scheduled_date || b.date || '';

    if (dateA !== dateB) {
      return dateA.localeCompare(dateB);
    }

    return a.start_time.localeCompare(b.start_time);
  });
}

// ============================================================================
// Search and Filter
// ============================================================================

/**
 * Search booking by text (customer name, phone, booking ref)
 */
export function searchBooking(
  booking: OneTimeBooking | RecurringBooking,
  searchTerm: string
): boolean {
  if (!searchTerm) return true;

  const term = searchTerm.toLowerCase();
  const customerName = booking.customer?.name?.toLowerCase() || booking.user?.name?.toLowerCase() || '';
  const customerPhone = booking.customer?.phone || booking.user?.phone || '';
  const reference = booking.booking_reference?.toLowerCase() || '';

  return (
    customerName.includes(term) ||
    customerPhone.includes(term) ||
    reference.includes(term)
  );
}

// ============================================================================
// Duration Calculations
// ============================================================================

/**
 * Calculate duration between two times in minutes
 */
export function calculateDuration(startTime: string, endTime: string): number {
  const [startHours, startMinutes] = startTime.split(':').map(Number);
  const [endHours, endMinutes] = endTime.split(':').map(Number);

  const startTotalMinutes = startHours * 60 + startMinutes;
  const endTotalMinutes = endHours * 60 + endMinutes;

  let duration = endTotalMinutes - startTotalMinutes;
  if (duration <= 0) duration += 24 * 60; // Handle overnight wrap-around
  return duration;
}

/**
 * Format duration as human-readable string
 * @example formatDuration(90) => '1h 30m'
 */
export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

/**
 * Calculate and format duration from start and end times
 * @example formatDurationFromTimes('10:00:00', '11:30:00') => '1h 30m'
 */
export function formatDurationFromTimes(startTime: string, endTime: string): string {
  const minutes = calculateDuration(startTime, endTime);
  return formatDuration(minutes);
}

// ============================================================================
// Export all helpers
// ============================================================================

export const bookingHelpers = {
  // Time
  formatTime,
  formatTimeSlot,
  formatDate,
  formatDateTime,
  formatDateTimeString,
  getRelativeDate,
  calculateDuration,
  formatDuration,
  formatDurationFromTimes,

  // Status
  getStatusColor,
  getStatusIcon,
  getPaymentStatusColor,
  getRefundStatusColor,
  getRefundStatusLabel,
  getTypeColor,
  getTypeBadge,

  // Block reasons
  getBlockReasonDisplay,
  getBlockReasonIcon,

  // Recurring
  calculateProgress,
  getNextSession,
  formatFrequency,

  // Validation
  canCheckIn,
  canReschedule,
  canCancel,
  canUnblock,

  // Currency
  formatCurrency,
  formatVendorShare,

  // Grouping/Sorting
  groupByDate,
  sortByDateTime,

  // Search
  searchBooking,
};

export default bookingHelpers;
